/**
 * v22 BOLT-110 — the location warning gets an ACTION.
 *
 * Today a blocked player reads "Bolt needs your location. Turn on GPS and allow
 * location to play." and has nowhere to go. This is the decision layer that
 * turns that dead end into one tap that does the right thing for the state
 * they are actually in.
 *
 * BE HONEST ABOUT THE OS: "a button re-prompts the same request" is only
 * literally possible in `notDetermined`. Once a user has DENIED location,
 * neither iOS nor any browser will re-show the native dialog. Settings is the
 * only door, so that is what the button becomes. iOS can deep-link straight to
 * Bolt's location toggle; the web can only tell the player where to look,
 * because browsers cannot open their own site settings programmatically.
 *
 * Ported to Swift in `LocationRecovery.swift`; unit-tested on both sides.
 */
export type LocationPermission =
  | 'notDetermined' // never asked, or the prompt was dismissed before answering
  | 'denied' // denied or restricted by the OS / browser
  | 'reduced' // iOS "approximate location": granted, but not precise enough to play
  | 'precise'; // good to go

export type RecoveryAction =
  | 'request' // the native prompt, which only works from notDetermined
  | 'openSettings' // iOS deep-links; web shows the site-settings hint
  | 'requestPreciseOnce' // iOS requestTemporaryFullAccuracyAuthorization
  | 'none';

/** The whole decision, in one testable place. */
export function recoveryAction(permission: LocationPermission): RecoveryAction {
  switch (permission) {
    case 'notDetermined':
      return 'request';
    case 'denied':
      return 'openSettings';
    case 'reduced':
      return 'requestPreciseOnce';
    case 'precise':
      return 'none';
  }
}

/** The button label for each action. Verbs, not adjectives. */
export function recoveryLabel(action: RecoveryAction): string {
  switch (action) {
    case 'request':
      return 'Allow location';
    case 'openSettings':
      return 'Open Settings';
    case 'requestPreciseOnce':
      return 'Turn on precise location';
    case 'none':
      return '';
  }
}

/** Why we are asking, in the player's terms. */
export function recoveryReason(permission: LocationPermission): string {
  switch (permission) {
    case 'notDetermined':
      return 'Bolt needs your location to play.';
    case 'denied':
      return 'Location is off for Bolt. Turn it back on to play.';
    case 'reduced':
      return 'Bolt has your approximate location. It needs precise location to play.';
    case 'precise':
      return '';
  }
}

/**
 * v22 BOLT-110 — the troubleshooting steps behind the "why?" affordance.
 * Paired with BOLT-115's live reason and accuracy, the player is told exactly
 * what is wrong AND what to do about it, instead of hitting a wall.
 */
export const LOCATION_TROUBLESHOOTING: readonly string[] = [
  'Open Settings, tap Bolt, and turn Precise Location on.',
  'Step outside, or stand near a window, so the fix can sharpen.',
  // v28 BOLT-154 (Jake), verbatim: "Change 'still stuck..' on the waiting for
  // gps help menu to 'Still Stuck? Try quitting and restarting the app.'"
  // The old line pointed at "the button above", which is not always there: the
  // web cannot re-prompt after a denial and renders no button at all, so the
  // last resort pointed at nothing in exactly the state that needed it most.
  'Still Stuck? Try quitting and restarting the app.',
];
